/**
 * Bounded fetch for anonymous public bootstrap (`/public/settings`, `/public/strings`).
 *
 * Timeout budget (per attempt): 4s. These are same-origin the API config endpoints; if they
 * have not answered in 4s the API is effectively down for cold-boot purposes. Longer
 * waits only prolong a blank/splash screen without improving recovery odds.
 *
 * Retries: 3 attempts with 250ms / 500ms backoff. Total wall-clock ≈ 4+0.25+4+0.5+4 < 13s,
 * still far below the browser's default hang (~300s) that previously left users on a
 * blank page. Authenticated settings already self-heal via the 60s poll — do not reuse
 * this helper there.
 */

/** Per-attempt AbortController timeout for public bootstrap fetches. */
export const PUBLIC_BOOT_FETCH_TIMEOUT_MS = 4_000;

/** Including the first attempt. */
export const PUBLIC_BOOT_FETCH_MAX_ATTEMPTS = 3;

const PUBLIC_BOOT_FETCH_BACKOFF_MS = [250, 500] as const;

export type PublicBootFetchOutcome = 'ok' | 'unreachable';

export type PublicBootFetchResult = {
  response: Response | null;
  outcome: PublicBootFetchOutcome;
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchOnce(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, { credentials: 'omit', signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetches a public bootstrap URL with per-attempt timeout and exponential backoff.
 * Returns `outcome: 'unreachable'` when every attempt fails (network, abort, or throw).
 * An HTTP error status (4xx/5xx) still counts as `ok` — the API was reached.
 */
export async function fetchPublicBootResource(
  url: string,
  {
    timeoutMs = PUBLIC_BOOT_FETCH_TIMEOUT_MS,
    maxAttempts = PUBLIC_BOOT_FETCH_MAX_ATTEMPTS,
    label = 'public bootstrap',
  }: { timeoutMs?: number; maxAttempts?: number; label?: string } = {},
): Promise<PublicBootFetchResult> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const response = await fetchOnce(url, timeoutMs);
      return { response, outcome: 'ok' };
    } catch (error) {
      lastError = error;

      if (attempt < maxAttempts) {
        const backoff = PUBLIC_BOOT_FETCH_BACKOFF_MS[attempt - 1] ?? PUBLIC_BOOT_FETCH_BACKOFF_MS.at(-1)!;
        console.warn(`[settings] ${label} attempt ${attempt}/${maxAttempts} failed; retrying in ${backoff}ms:`, error);
        await sleep(backoff);
      }
    }
  }

  console.warn(`[settings] ${label} failed after ${maxAttempts} attempts; continuing on bundled defaults:`, lastError);

  return { response: null, outcome: 'unreachable' };
}
